import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import {
  MdAdd,
  MdDeleteOutline,
  MdOutlineEdit,
  MdOutlineRoute,
  MdOutlineWarningAmber,
} from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import TextInput from "../../../../../../../Components/UI/Form/TextInput";
import { useSnackBar } from "../../../../../../../hooks/useSnackBar";
import {
  createDeliveryRoute,
  deleteDeliveryRoute,
  fetchDeliveryRoutes,
  updateDeliveryRoute,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRoutes.services";
import AreaSearch from "./AreaSearch";
import RouteMap from "./RouteMap";
import {
  areasFor,
  boundsOf,
  circleBounds,
  codesTouching,
  loadPostalAreas,
  mergePrefixes,
  sharedAreas,
  toggleArea,
} from "./postalAreas";

const ICON_BTN =
  "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[6px] disabled:cursor-default disabled:opacity-30";
const PANEL =
  "flex min-h-[0px] flex-col overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface";
const PANEL_HEAD =
  "flex min-h-[38px] shrink-0 items-center gap-[8px] border-b-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[7px]";
const PANEL_TITLE =
  "flex items-center gap-[6px] font-jakarta text-[11.5px] font-bold leading-[1.3] text-ui-text";
const ROW = "border-b-[1px] border-ui-border-subtle last:border-b-0";

const ROUTE_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#16a34a",
  "#ea580c",
  "#0891b2",
  "#db2777",
  "#ca8a04",
];

const nextRouteColor = (routes) => {
  const used = new Set(routes.map((route) => route.color.toLowerCase()));
  return ROUTE_COLORS.find((color) => !used.has(color)) || ROUTE_COLORS[0];
};

const errorText = (error) => error?.response?.data?.message;
const plural = (n, word, many = `${word}s`) =>
  `${n} ${n === 1 ? word : many}`;
const SUMMARY_LIMIT = 4;
const prefixSummary = (prefixes) =>
  prefixes.length > SUMMARY_LIMIT
    ? `${prefixes.slice(0, SUMMARY_LIMIT).join(", ")} +${
        prefixes.length - SUMMARY_LIMIT
      } more`
    : prefixes.join(", ") || "No postal areas";

const sharedText = (shared) => {
  const names = [...new Set([...shared.values()].flat())];
  const who =
    names.length > 2
      ? `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`
      : names.join(" and ");
  return `${plural(shared.size, "area")} ${
    shared.size === 1 ? "is" : "are"
  } also in ${who}.`;
};

const ColorDot = ({ color }) => (
  <span
    aria-hidden="true"
    className="h-[10px] w-[10px] shrink-0 rounded-full"
    style={{ background: color || "var(--color-brand)" }}
  />
);

function RouteEditor({
  draft,
  areas,
  places,
  hovered,
  shared,
  onName,
  onAddPrefixes,
  onRemovePrefixes,
  onRemoveShared,
  onPreview,
  onHover,
}) {
  const tagsRef = useRef(null);
  const count = draft.prefixes.length;
  const highlighted = draft.prefixes.find((prefix) =>
    hovered?.startsWith(prefix),
  );
  const sharedOwners = (prefix) => [
    ...new Set(
      [...shared.entries()]
        .filter(([code]) => code.startsWith(prefix))
        .flatMap(([, names]) => names),
    ),
  ];

  useEffect(() => {
    if (!highlighted) return;
    tagsRef.current
      ?.querySelector(`[data-prefix="${highlighted}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [highlighted]);

  return (
    <div
      className="flex flex-col gap-[10px] border-l-[3px] bg-brand-light px-[12px] py-[10px]"
      style={{ borderLeftColor: draft.color || "var(--color-brand)" }}
      aria-label={draft.id ? "Edit route" : "New route"}
      role="group"
    >
      <TextInput
        label={draft.id ? "Route name" : "New route name"}
        name="routeName"
        value={draft.name}
        onChange={(event) => onName(event.target.value)}
        placeholder="e.g. Sherbrooke"
        inputProps={{ id: "routeName", maxLength: 100, autoFocus: true }}
      />
      <AreaSearch
        areas={areas}
        places={places}
        onAdd={onAddPrefixes}
        onPreview={onPreview}
      />
      <div className="flex flex-col gap-[6px]">
        <div className="flex items-center gap-[8px]">
          <p
            className={`m-[0px] flex-1 font-manrope text-[11px] font-medium ${
              count ? "text-ui-text-secondary" : "text-ui-text-muted"
            }`}
          >
            {count
              ? plural(count, "postal prefix", "postal prefixes")
              : "Search above, click areas on the map, or draw around them."}
          </p>
          {count > 1 && (
            <button
              type="button"
              onClick={() => onRemovePrefixes(draft.prefixes)}
              className="font-manrope text-[11px] font-semibold text-brand hover:underline"
            >
              Remove all
            </button>
          )}
        </div>
        {shared.size > 0 && (
          <div
            role="status"
            className="flex items-start gap-[6px] rounded-[6px] border-[1px] border-state-warning-border bg-state-warning-bg px-[8px] py-[6px] font-manrope text-state-warning-text"
          >
            <MdOutlineWarningAmber className="mt-[1px] h-[15px] w-[15px] shrink-0" />
            <p className="m-[0px] flex-1 text-[11px] font-semibold leading-[1.4]">
              {sharedText(shared)}
            </p>
            <button
              type="button"
              onClick={onRemoveShared}
              className="shrink-0 text-[11px] font-bold underline"
            >
              Remove {shared.size === 1 ? "it" : "them"}
            </button>
          </div>
        )}
        {count > 0 && (
          <ul
            ref={tagsRef}
            aria-label="Postal prefixes"
            className="thin__scrollbar m-[0px] flex max-h-[112px] list-none flex-wrap gap-[5px] overflow-y-auto p-[0px]"
          >
            {draft.prefixes.map((prefix) => {
              const owners = sharedOwners(prefix);
              return (
                <li
                  key={prefix}
                  data-prefix={prefix}
                  title={owners.length ? `Also in ${owners.join(", ")}` : undefined}
                  onMouseEnter={() => onHover(prefix)}
                  onMouseLeave={() => onHover(null)}
                  className={`inline-flex h-[24px] items-center gap-[2px] rounded-[5px] border-[1px] pl-[7px] pr-[2px] font-manrope text-[11px] font-bold tracking-[0.3px] ${
                    prefix === highlighted
                      ? "border-brand bg-ui-surface text-brand"
                      : owners.length
                        ? "border-state-warning-border bg-state-warning-bg text-state-warning-text"
                        : "border-ui-border bg-ui-surface text-ui-text"
                  }`}
                >
                  {prefix}
                  <button
                    type="button"
                    aria-label={`Remove ${prefix}`}
                    title={`Remove ${prefix}`}
                    onClick={() => onRemovePrefixes([prefix])}
                    className="grid h-[20px] w-[20px] place-items-center rounded-[4px] text-ui-text-muted hover:bg-ui-surface-sunken hover:text-state-danger-text"
                  >
                    <IoClose className="h-[12px] w-[12px]" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function ManageRoutesModal({ open, onClose }) {
  const notify = useSnackBar();
  const closeRef = useRef(null);
  const rowRefs = useRef({});
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [hoveredRouteId, setHoveredRouteId] = useState(null);
  const [areas, setAreas] = useState(null);
  const [places, setPlaces] = useState([]);
  const [focus, setFocus] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    const [{ data, error }, postal] = await Promise.all([
      fetchDeliveryRoutes(),
      loadPostalAreas().catch(() => null),
    ]);
    if (error || !postal) setLoadFailed(true);
    else {
      setRoutes(data);
      setAreas(postal.areas);
      setPlaces(postal.places);
    }
    setLoading(false);
  }, []);

  const showPreview = useCallback((next) => {
    setPreview(next);
    if (next) setFocus(circleBounds(next.center, next.km));
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    setConfirmingId(null);
    setHighlightedId(null);
    setFocus(null);
    load();
    closeRef.current?.focus();
  }, [open, load]);

  useEffect(() => {
    if (!open || draft) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, draft, onClose]);

  useEffect(() => {
    if (highlightedId == null) return;
    rowRefs.current[highlightedId]?.scrollIntoView?.({ block: "nearest" });
  }, [highlightedId]);

  if (!open) return null;

  const busy = !!draft || loading || loadFailed;
  const canSave =
    !!draft && draft.name.trim() !== "" && draft.prefixes.length > 0 && !saving;

  const activeRouteId = draft ? null : hoveredRouteId ?? highlightedId;

  const showRoute = (route) => {
    setConfirmingId(null);
    setHighlightedId(route.id);
    const bounds = boundsOf(areasFor(areas, route.prefixes));
    if (bounds) setFocus(bounds);
  };

  const startNew = () => {
    setConfirmingId(null);
    setHighlightedId(null);
    setHoveredRouteId(null);
    setHovered(null);
    setDraft({ name: "", color: nextRouteColor(routes), prefixes: [] });
  };

  const startEdit = (route) => {
    setConfirmingId(null);
    setHighlightedId(null);
    setHoveredRouteId(null);
    setHovered(null);
    setDraft({ ...route });
    const bounds = boundsOf(areasFor(areas, route.prefixes));
    if (bounds) setFocus(bounds);
  };

  const addPrefixes = (prefixes) =>
    setDraft(
      (current) =>
        current && {
          ...current,
          prefixes: mergePrefixes([...current.prefixes, ...prefixes]),
        },
    );

  const removePrefixes = (prefixes) => {
    setHovered(null);
    setDraft(
      (current) =>
        current && {
          ...current,
          prefixes: current.prefixes.filter(
            (prefix) => !prefixes.includes(prefix),
          ),
        },
    );
  };

  const toggleCode = (code) =>
    setDraft(
      (current) =>
        current && {
          ...current,
          prefixes: toggleArea(areas, current.prefixes, code),
        },
    );

  const addDrawn = (geometry) => {
    const codes = codesTouching(areas, geometry);
    if (codes.length) addPrefixes(codes);
    return codes.length;
  };

  const addFound = (prefixes) => {
    addPrefixes(prefixes);
    const bounds = boundsOf(areasFor(areas, prefixes));
    if (bounds) setFocus(bounds);
  };

  const shared = draft
    ? sharedAreas(
        areas,
        draft.prefixes,
        routes.filter((route) => route.id !== draft.id),
      )
    : new Map();

  const removeShared = () =>
    setDraft(
      (current) =>
        current && {
          ...current,
          prefixes: mergePrefixes(
            areasFor(areas, current.prefixes)
              .map((area) => area.code)
              .filter((code) => !shared.has(code)),
          ),
        },
    );

  const save = async () => {
    setSaving(true);
    const route = {
      ...draft,
      paths: areasFor(areas, draft.prefixes).flatMap((area) => area.paths),
    };
    const { data, error } = draft.id
      ? await updateDeliveryRoute(draft.id, route)
      : await createDeliveryRoute(route);
    setSaving(false);
    if (error) {
      notify.error(errorText(error) || "Could not save the route");
      return;
    }
    notify.success(
      draft.id ? `${draft.name.trim()} updated` : `${draft.name.trim()} created`,
    );
    if (data?.id) {
      setRoutes((list) =>
        draft.id
          ? list.map((item) => (item.id === draft.id ? data : item))
          : [...list, data],
      );
    } else {
      load();
    }
    setDraft(null);
  };

  const remove = async (route) => {
    setDeletingId(route.id);
    const { error } = await deleteDeliveryRoute(route.id);
    setDeletingId(null);
    setConfirmingId(null);
    if (error) {
      notify.error(errorText(error) || "Could not delete the route");
      return;
    }
    notify.success(`${route.name} deleted`);
    setRoutes((list) => list.filter((item) => item.id !== route.id));
  };

  const editor = (
    <RouteEditor
      draft={draft}
      areas={areas}
      places={places}
      hovered={hovered}
      shared={shared}
      onName={(name) => setDraft((current) => ({ ...current, name }))}
      onAddPrefixes={addFound}
      onRemovePrefixes={removePrefixes}
      onRemoveShared={removeShared}
      onPreview={showPreview}
      onHover={setHovered}
    />
  );

  const routeRow = (route) => {
    if (draft?.id === route.id) {
      return (
        <li key={route.id} className={ROW}>
          {editor}
        </li>
      );
    }

    const highlighted = activeRouteId === route.id;
    return (
      <li
        key={route.id}
        ref={(node) => {
          rowRefs.current[route.id] = node;
        }}
        onMouseEnter={() => setHoveredRouteId(route.id)}
        onMouseLeave={() => setHoveredRouteId(null)}
        className={`${ROW} border-l-[3px] px-[12px] py-[9px] ${
          highlighted ? "bg-brand-light" : ""
        }`}
        style={{ borderLeftColor: route.color || "var(--color-brand)" }}
        aria-current={highlighted ? "true" : undefined}
      >
        {confirmingId === route.id ? (
          <div className="flex flex-col gap-[8px]">
            <p className="m-[0px] font-manrope text-[12px] font-medium text-ui-text">
              Delete <b>{route.name}</b>? This can't be undone.
            </p>
            <div className="flex gap-[8px]">
              <BoltButton
                type="button"
                variant="outline"
                size="sm"
                label="Keep"
                onClick={() => setConfirmingId(null)}
                disabled={deletingId === route.id}
              />
              <BoltButton
                type="button"
                variant="danger"
                size="sm"
                label={deletingId === route.id ? "Deleting…" : "Delete"}
                onClick={() => remove(route)}
                disabled={deletingId === route.id}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-[8px]">
            <button
              type="button"
              onClick={() => showRoute(route)}
              title="Show on map"
              className="flex min-w-[0px] flex-1 items-center gap-[8px] text-left"
            >
              <ColorDot color={route.color} />
              <span className="min-w-[0px] flex-1">
                <span className="block truncate font-manrope text-[12.5px] font-bold text-ui-text">
                  {route.name}
                </span>
                <span
                  className="block truncate font-manrope text-[10.5px] font-medium text-ui-text-muted"
                  title={route.prefixes.join(", ")}
                >
                  {prefixSummary(route.prefixes)}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => startEdit(route)}
              disabled={busy}
              aria-label={`Edit route ${route.name}`}
              title="Edit route"
              className={`${ICON_BTN} text-ui-text-muted hover:bg-ui-surface-sunken hover:text-brand`}
            >
              <MdOutlineEdit className="h-[16px] w-[16px]" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmingId(route.id)}
              disabled={busy}
              aria-label={`Delete route ${route.name}`}
              title="Delete route"
              className={`${ICON_BTN} text-state-danger-text hover:bg-state-danger-bg`}
            >
              <MdDeleteOutline className="h-[16px] w-[16px]" />
            </button>
          </div>
        )}
      </li>
    );
  };

  const listPanel = (
    <section
      className={`${PANEL} flex-1`}
      aria-label="Routes"
    >
      <div className={PANEL_HEAD}>
        <span className={PANEL_TITLE}>
          <MdOutlineRoute className="h-[14px] w-[14px] text-ui-text-muted" />
          Routes
        </span>
        <span className="font-manrope text-[10.5px] font-medium text-ui-text-muted">
          {loading || loadFailed ? "" : plural(routes.length, "route")}
        </span>
        <button
          type="button"
          onClick={startNew}
          disabled={busy}
          className="ml-auto inline-flex h-[28px] items-center gap-[5px] rounded-[6px] px-[9px] font-manrope text-[12px] font-medium text-brand hover:bg-state-info-bg disabled:cursor-default disabled:opacity-50"
        >
          <MdAdd className="h-[13px] w-[13px]" />
          Add route
        </button>
      </div>
      <div className="thin__scrollbar min-h-[0px] flex-1 overflow-y-auto">
        {draft && !draft.id && editor}
        {loading ? (
          <div className="flex justify-center py-[32px]">
            <CircularLoader />
          </div>
        ) : loadFailed ? (
          <div className="flex flex-col items-center gap-[8px] px-[14px] py-[28px] text-center">
            <p className="m-[0px] font-manrope text-[12px] font-medium text-ui-text-muted">
              Couldn't load your routes.
            </p>
            <BoltButton
              type="button"
              variant="outline"
              size="sm"
              label="Try again"
              onClick={load}
            />
          </div>
        ) : routes.length === 0 ? (
          !draft && (
            <p className="m-[0px] px-[14px] py-[28px] text-center font-manrope text-[12px] font-medium text-ui-text-muted">
              No routes yet. Add one and pick the postal areas it covers.
            </p>
          )
        ) : (
          <ul className="m-[0px] list-none p-[0px]">{routes.map(routeRow)}</ul>
        )}
      </div>
    </section>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end justify-center bg-black/40 pt-[10px] sm:items-start sm:p-[14px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manageRoutesTitle"
        className="flex h-[calc(100dvh-10px)] w-full max-w-[1320px] flex-col overflow-hidden rounded-t-[16px] border-[1px] border-b-0 border-ui-border bg-ui-page font-manrope text-[12px] text-ui-text shadow-ui-md sm:h-[calc(100dvh-28px)] sm:rounded-[11px] sm:border-b-[1px]"
      >
        <header className="flex shrink-0 items-center gap-[11px] border-b-[1px] border-ui-border bg-ui-surface px-[14px] py-[12px]">
          <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[8px] bg-brand-light text-brand">
            <MdOutlineRoute className="h-[18px] w-[18px]" />
          </span>
          <div className="grid min-w-[0px] flex-1 gap-[2px]">
            <h2
              id="manageRoutesTitle"
              className="m-[0px] font-jakarta text-[15px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ui-text"
            >
              Delivery routes
            </h2>
            <span className="font-manrope text-[11px] font-medium leading-[1.35] text-ui-text-muted sm:truncate">
              All your routes on one map. Click a route to find it, or the pencil to edit it.
            </span>
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text"
          >
            <IoClose className="h-[18px] w-[18px]" />
          </button>
        </header>

        <div className="flex min-h-[0px] flex-1 flex-col gap-[10px] overflow-hidden p-[10px] lg:flex-row-reverse [@media(max-height:500px)]:flex-row-reverse">
          <div className={`${PANEL} h-[45%] shrink-0 lg:h-auto lg:flex-1 [@media(max-height:500px)]:h-auto [@media(max-height:500px)]:flex-1`}>
            <RouteMap
              key={loading ? "loading" : "ready"}
              areas={areas ?? []}
              routes={routes}
              draft={draft}
              focus={focus}
              preview={draft ? preview : null}
              activeRouteId={activeRouteId}
              onSelectRoute={(id) => {
                setConfirmingId(null);
                setHighlightedId(id);
              }}
              onHoverRoute={setHoveredRouteId}
              onDraw={addDrawn}
              hovered={draft ? hovered : null}
              onToggleArea={toggleCode}
              onHover={setHovered}
            />
          </div>
          <div className="flex min-h-[0px] flex-1 flex-col lg:w-[340px] lg:flex-none [@media(max-height:500px)]:w-[260px] [@media(max-height:500px)]:flex-none">
            {listPanel}
          </div>
        </div>

        <footer className="flex shrink-0 flex-wrap items-center gap-[8px] border-t-[1px] border-ui-border bg-ui-surface px-[14px] pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-[10px] sm:py-[10px]">
          <span className="flex-[1_1_180px] font-manrope text-[11px] font-medium text-ui-text-muted">
            {draft
              ? "Nothing is saved until you save the route."
              : "Each route is the area its deliveries cover."}
          </span>
          <div className="ml-auto flex gap-[8px]">
            {draft ? (
              <>
                <BoltButton
                  type="button"
                  variant="outline"
                  size="sm"
                  label="Cancel"
                  onClick={() => setDraft(null)}
                  disabled={saving}
                />
                <BoltButton
                  type="button"
                  variant="primary"
                  size="sm"
                  label={
                    saving ? "Saving…" : draft.id ? "Save changes" : "Save route"
                  }
                  onClick={save}
                  disabled={!canSave}
                />
              </>
            ) : (
              <BoltButton
                type="button"
                variant="outline"
                size="sm"
                label="Close"
                onClick={onClose}
              />
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
